/**
 * Cloudinary Image Utilities
 * Handles Cloudinary image objects and URL validation
 */
import { getPrimaryProductImageUrl } from './productImages';

/**
 * Extract URL from Cloudinary image object
 * @param {Object|string} image - Cloudinary image object or string URL
 * @returns {string|null} - Extracted URL or null
 */
export const extractCloudinaryUrl = (image) => {
  if (!image) return null;
  
  if (typeof image === 'string') {
    return image.trim() !== '' ? image : null;
  }
  
  if (typeof image === 'object' && image !== null) {
    // Handle Cloudinary object with metadata
    return image.url || image.fullPath || image.src || image.path || null;
  }
  
  return null;
};

/**
 * Process array of Cloudinary images
 * @param {Array} images - Array of Cloudinary image objects or strings
 * @returns {Array} - Array of valid URLs
 */
export const processCloudinaryImages = (images) => {
  if (!Array.isArray(images) || images.length === 0) {
    return [];
  }
  
  return images
    .map(img => extractCloudinaryUrl(img))
    .filter(Boolean);
};

/**
 * Extract Cloudinary public_id from a delivery URL.
 * @param {string} url
 * @returns {string|null}
 */
export const extractCloudinaryPublicId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed.includes('cloudinary.com')) return null;

  try {
    const match = trimmed.match(
      /\/image\/upload(?:\/[^/]+)*\/((?:v\d+\/)?[^?#]+)/i
    );
    if (!match?.[1]) return null;
    let publicId = decodeURIComponent(match[1]);
    if (publicId.endsWith('.jpg') || publicId.endsWith('.jpeg') || publicId.endsWith('.png') || publicId.endsWith('.webp') || publicId.endsWith('.gif')) {
      publicId = publicId.replace(/\.(jpe?g|png|webp|gif)$/i, '');
    }
    return publicId || null;
  } catch {
    return null;
  }
};

/**
 * Validate Cloudinary URL
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid Cloudinary URL
 */
export const isValidCloudinaryUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  return url.includes('cloudinary.com') || url.includes('res.cloudinary.com');
};

/**
 * Get first valid Cloudinary image URL
 * @param {Object} product - Product object
 * @returns {string|null} - First valid image URL or null
 */
export const getFirstCloudinaryImage = (product) => {
  if (!product) return null;

  const primary = getPrimaryProductImageUrl(product);
  if (primary) {
    const fromPrimary = extractCloudinaryUrl(primary);
    if (fromPrimary) return fromPrimary;
  }

  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstUrl = extractCloudinaryUrl(product.images[0]);
    if (firstUrl) return firstUrl;
  }

  if (product.image) {
    const imageUrl = extractCloudinaryUrl(product.image);
    if (imageUrl) return imageUrl;
  }

  return null;
};

/**
 * Get all valid Cloudinary image URLs
 * @param {Object} product - Product object
 * @returns {Array} - Array of valid image URLs
 */
export const getAllCloudinaryImages = (product) => {
  if (!product) return [];
  
  const urls = [];
  
  // Process images array
  if (product.images && Array.isArray(product.images)) {
    urls.push(...processCloudinaryImages(product.images));
  }
  
  // If no images from array, try single image
  if (urls.length === 0 && product.image) {
    const imageUrl = extractCloudinaryUrl(product.image);
    if (imageUrl) urls.push(imageUrl);
  }
  
  return urls;
};

/**
 * Debug Cloudinary image data
 * @param {Object} product - Product object
 * @param {string} context - Context for debugging
 */
export const debugCloudinaryImages = (product, context = 'Unknown') => {
  console.log(`🔍 Cloudinary Debug [${context}]:`, {
    productName: product?.name || 'Unknown',
    hasImages: !!(product?.images && product.images.length > 0),
    imagesCount: product?.images?.length || 0,
    hasImage: !!product?.image,
    imageType: typeof product?.image,
    processedUrls: getAllCloudinaryImages(product),
    cloudinaryUrls: getAllCloudinaryImages(product).filter(url => isValidCloudinaryUrl(url)),
    firstImage: getFirstCloudinaryImage(product)
  });
};

/**
 * Test Cloudinary image loading
 * @param {string} url - Image URL to test
 * @returns {Promise<boolean>} - True if image loads successfully
 */
export const testCloudinaryImageLoad = (url) => {
  return new Promise((resolve) => {
    if (!url || !isValidCloudinaryUrl(url)) {
      console.error('❌ Invalid Cloudinary URL:', url);
      resolve(false);
      return;
    }
    
    const img = new Image();
    const startTime = Date.now();
    
    img.onload = () => {
      const loadTime = Date.now() - startTime;
      console.log(`✅ Cloudinary image loaded successfully: ${url} (${loadTime}ms)`);
      resolve(true);
    };
    
    img.onerror = () => {
      console.error(`❌ Cloudinary image failed to load: ${url}`);
      resolve(false);
    };
    
    img.src = url;
  });
};

/**
 * Batch test multiple Cloudinary images
 * @param {Array} urls - Array of image URLs to test
 * @returns {Promise<Array>} - Array of test results
 */
export const batchTestCloudinaryImages = async (urls) => {
  const results = await Promise.all(
    urls.map(async (url) => ({
      url,
      success: await testCloudinaryImageLoad(url),
      timestamp: new Date().toISOString()
    }))
  );
  
  console.log('🧪 Cloudinary batch test results:', results);
  return results;
};

export function normalizeCloudinaryUnusedImage(item) {
  if (!item || typeof item !== 'object') return null;
  const url =
    item.url ||
    item.secure_url ||
    item.secureUrl ||
    extractCloudinaryUrl(item) ||
    '';
  const publicId =
    item.publicId ||
    item.public_id ||
    extractCloudinaryPublicId(url) ||
    '';
  if (!publicId || !url) return null;
  return {
    publicId: String(publicId).trim(),
    url: String(url).trim(),
    bytes: Number(item.bytes) || 0,
    format: item.format ? String(item.format) : '',
    width: Number(item.width) || 0,
    height: Number(item.height) || 0,
    createdAt: item.createdAt || item.created_at || null,
  };
}

export function normalizeCloudinaryUnusedImagesResponse(res) {
  const data = res?.data && typeof res.data === 'object' ? res.data : res;
  const list = Array.isArray(data?.images)
    ? data.images
    : Array.isArray(data?.unused)
    ? data.unused
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
    ? data
    : [];
  const seen = new Set();
  const images = [];
  for (const row of list) {
    const normalized = normalizeCloudinaryUnusedImage(row);
    if (!normalized) continue;
    const key = normalized.publicId.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    images.push(normalized);
  }
  return {
    images,
    totalScanned: Number(data?.totalScanned ?? data?.scanned ?? 0) || images.length,
    totalUnused: Number(data?.totalUnused ?? data?.unusedCount ?? images.length) || images.length,
  };
}

const cloudinaryUtils = {
  extractCloudinaryUrl,
  extractCloudinaryPublicId,
  processCloudinaryImages,
  isValidCloudinaryUrl,
  normalizeCloudinaryUnusedImage,
  normalizeCloudinaryUnusedImagesResponse,
  getFirstCloudinaryImage,
  getAllCloudinaryImages,
  debugCloudinaryImages,
  testCloudinaryImageLoad,
  batchTestCloudinaryImages
};

export default cloudinaryUtils;
